import { useId, useState, type ReactNode } from 'react';
import {
  FREE_REASON_MIN_LENGTH,
  isFreeReasonAcceptable,
  REASON_MAX_LENGTH,
  type RejectionReason,
} from '@domain/entities/RuntimeConfig';
import { selectClass, textareaClass } from '../design-system';
import styles from './ReasonPicker.module.css';

/**
 * El motivo de un rechazo, una reapertura o una cancelación.
 *
 * La lista del catálogo, como siempre, y al final «Otro motivo»: al elegirlo
 * aparece una caja para escribirlo. Hasta el 7 oct 2026 sólo había lista, y el
 * motivo de verdad muchas veces no estaba en ella — quien rechazaba elegía el
 * que más se parecía, y la bitácora guardaba un motivo que nadie había dado.
 *
 * **Quien usa esto sólo ve un motivo que vale, o nada.** `onChange` entrega el
 * texto del catálogo, el texto escrito cuando ya alcanza el mínimo, o la cadena
 * vacía. Por eso las siete ventanas que lo usan no cambiaron su regla: el botón
 * de confirmar sigue apagado mientras el motivo esté vacío, y un texto de tres
 * letras *es* vacío para ellas. Lo que se lleva escrito lo guarda este
 * componente, no la ventana.
 *
 * El mínimo lo exige el servidor —`assertAcceptableReason`—; aquí sólo se avisa
 * antes, para no dejar pulsar un botón que iba a contestar con un error.
 */

/** El valor de la opción «Otro motivo». Nadie da de alta un motivo que se llame así. */
const OTHER = '__otro__';

interface ReasonPickerProps {
  /** El id que pone `Field`, para que su etiqueta apunte a la lista. */
  readonly id: string;
  readonly reasons: readonly RejectionReason[];
  /** El motivo vigente: uno del catálogo, lo escrito si ya vale, o `''`. */
  readonly value: string;
  readonly onChange: (reason: string) => void;
}

export const ReasonPicker = ({ id, reasons, value, onChange }: ReasonPickerProps): ReactNode => {
  const textId = useId();
  const [free, setFree] = useState(false);
  const [draft, setDraft] = useState('');

  const written = draft.trim().length;
  const missing = FREE_REASON_MIN_LENGTH - written;

  return (
    <div className={styles.picker}>
      <select
        id={id}
        className={selectClass()}
        value={free ? OTHER : value}
        onChange={(event) => {
          const next = event.target.value;
          if (next === OTHER) {
            setFree(true);
            /* Lo que ya estuviera escrito de antes vuelve a contar, si alcanza. */
            onChange(isFreeReasonAcceptable(draft) ? draft.trim() : '');
            return;
          }
          setFree(false);
          onChange(next);
        }}
      >
        <option value="">Elige un motivo…</option>
        {reasons.map((option) => (
          <option key={option.id} value={option.text}>
            {option.text}
          </option>
        ))}
        <option value={OTHER}>Otro motivo (escribirlo)…</option>
      </select>

      {free && (
        <div className={styles.free}>
          <label className={styles.label} htmlFor={textId}>
            Escribe el motivo
          </label>
          <textarea
            id={textId}
            className={textareaClass()}
            value={draft}
            maxLength={REASON_MAX_LENGTH}
            placeholder="Con tus palabras: qué pasó y por qué."
            /* Se acaba de elegir «Otro motivo»: lo siguiente es escribir. */
            autoFocus
            onChange={(event) => {
              const text = event.target.value;
              setDraft(text);
              onChange(isFreeReasonAcceptable(text) ? text.trim() : '');
            }}
          />
          <div className={styles.foot}>
            {written > 0 && missing > 0 ? (
              <span className={styles.short} role="status">
                {missing === 1 ? 'Falta 1 carácter' : `Faltan ${String(missing)} caracteres`} para
                poder enviarlo.
              </span>
            ) : (
              /* Sin «se le muestra a la persona»: en una cancelación quien escribe
                 es la propia persona. Lo que vale para las siete ventanas es que
                 queda escrito. */
              <span>Mínimo {FREE_REASON_MIN_LENGTH} caracteres. Queda en la bitácora.</span>
            )}
            <span className={styles.count}>
              {draft.length}/{REASON_MAX_LENGTH}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
